import { forwardRef, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useMapbox } from "@/react/hooks/useMapbox";
import type { LocationOption, LocationType } from "@/react/hooks/useMapbox";
import type { FormInputProps } from "@circle-react-uikit/Form/FormInput";
import { FormTextSelect } from "../FormTextSelect";

interface LocationInputProps extends FormInputProps {
  shouldSkipLocationDetailRetrieve?: boolean;
  locationTypesToSearch?: LocationType[];
  inputPlaceholder?: string;
  initialDisplayValue?: string;
}

export const LocationInput = forwardRef<HTMLInputElement, LocationInputProps>(
  props => {
    const {
      name,
      shouldSkipLocationDetailRetrieve,
      locationTypesToSearch,
      className,
      inputPlaceholder,
      initialDisplayValue,
    } = props;
    const { setValue } = useFormContext();
    const [searchValue, setSearchValue] = useState<string | null>(null);

    const {
      locationOptions,
      handleSuggestionSelect,
      selectedMapboxId,
      debouncedSearch,
      value,
      displayValue,
      hasNoSuggestions,
    } = useMapbox({
      initialDisplayValue: initialDisplayValue,
      shouldSkipLocationDetailRetrieve,
      locationTypes: locationTypesToSearch,
    });

    useEffect(() => {
      if (value !== null) {
        setValue(name, value, { shouldValidate: true });
      }
    }, [value, name, setValue]);

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      void debouncedSearch(value);
    };

    return (
      <FormTextSelect<LocationOption>
        wrapperClassName={className}
        name={name}
        hideLabel
        label=""
        placeholder={
          inputPlaceholder ?? t("filters.location_filter.search_location")
        }
        emptyMessage={
          !searchValue
            ? t("filters.location_filter.search_helper")
            : hasNoSuggestions
              ? t("filters.location_filter.no_locations_found")
              : ""
        }
        options={locationOptions}
        onChange={handleSuggestionSelect}
        onSearchChange={handleSearchChange}
        value={selectedMapboxId}
        displayValue={displayValue}
        hideCaret
        shouldFilterOptions={false}
      />
    );
  },
);

LocationInput.displayName = "LocationInput";
